import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useUser } from "./../../Home/UserContext";
import { getRoleFromRoleId } from "./../../../utils/roleHelper";
import DashboardNavigation from "./../../DashboardNavigation/DashboardNavigation";
import {
  getAllInventaire,
  deleteInventaire,
} from "./../../../services/inventaireServices";
import useScrollToTop from "./../../../hooks/useScollToTop";
import { ToastContainer, toast } from "react-toastify";
import ButtonEdit from "./../../../components/Button/ButtonEdit";
import ButtonDelete from "./../../../components/Button/ButtonDelete";
import Button from "./../../../components/Button/Button";
import { FaPlus, FaSearch, FaBoxOpen } from "react-icons/fa";
import "react-toastify/dist/ReactToastify.css";
import EditInventaireModal from "./EditInventaireModal";
import AddInventaireModal from "./AddInventaireModal";
import { toAbsoluteInventairePhotoUrl as toAbsolute } from "../../../services/inventairePhotoServices";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";

export default function InventairePage() {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [inventaireData, setInventaireData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [currentInventaire, setCurrentInventaire] = useState(null);

  const fetchInventaire = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllInventaire();
      if (Array.isArray(data)) {
        setInventaireData(data);
      } else {
        throw new Error("Les données reçues ne sont pas un tableau");
      }
    } catch (error) {
      console.error("Erreur lors de la récupération de l'inventaire:", error);
      toast.error("Erreur lors de la récupération de l'inventaire");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInventaire();
  }, [fetchInventaire]);

  const filteredInventaire = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return inventaireData;
    return inventaireData.filter((item) =>
      `${item.nom || ""} ${item.categorie || ""}`.toLowerCase().includes(q)
    );
  }, [inventaireData, searchTerm]);

  const handleEditClick = (inventaire) => {
    setCurrentInventaire(inventaire);
    setShowEditModal(true);
  };

  const handleAddClick = () => setShowAddModal(true);

  const handleDeleteClick = async (id) => {
    if (!window.confirm("Supprimer cet objet de l’inventaire ?")) return;
    try {
      await deleteInventaire(id);
      setInventaireData((prev) => prev.filter((it) => it.id !== id));
      toast.success("Objet supprimé avec succès");
    } catch (error) {
      console.error("Erreur lors de la suppression de l'objet:", error);
      toast.error("Erreur lors de la suppression de l'objet");
    }
  };

  const FALLBACK = "/img/placeholder.png";

  return (
    <section className="w-full">
      {/* Offset navbar */}
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* Header page */}
                <HeaderTitle
                  role="Administrateur"
                  title="Gestion des Stocks"
                  align="center"
                  className="mb-1"
                />

        {/* Navigation */}
        <div className="mt-3 mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* ===== Carte principale ===== */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 mb-4">
          {/* Header gradient */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              {/* Titre + icône */}
              <div className="flex items-start sm:items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center text-white">
                  <FaBoxOpen />
                </div>
                <div className="text-white">
                  <p className="text-lg sm:text-xl font-semibold">
                    Gestion du stock
                  </p>
                  <p className="text-white/90 text-sm">
                    Recherchez, modifiez ou ajoutez des articles de
                    l’inventaire.
                  </p>
                </div>
              </div>

              {/* Recherche + bouton */}
              <div className="w-full sm:w-auto flex flex-col sm:flex-row gap-3 sm:items-center">
                <div className="relative sm:min-w-[320px]">
                  <input
                    id="search-inventaire"
                    type="text"
                    placeholder="Rechercher par nom ou catégorie…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full rounded-xl border-0 bg-white text-slate-900 pl-10 pr-3 py-2 shadow-md focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                </div>

                <Button
                  text="Ajouter un stock"
                  icon={FaPlus}
                  onClick={handleAddClick}
                  className="!bg-white !text-slate-900 !hover:bg-white/90 !rounded-xl !shadow-md gap-1"
                />
              </div>
            </div>
          </div>

          {/* Contenu blanc */}
          <div className="bg-white dark:bg-slate-900/60 px-4 pb-6 sm:px-6">
            {/* Loading */}
            {loading && (
              <div className="space-y-3 pt-4">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse rounded-xl border border-slate-200 dark:border-slate-800 p-5 bg-white/60 dark:bg-slate-900/40 h-20"
                  />
                ))}
              </div>
            )}

            {/* Empty state */}
            {!loading && filteredInventaire.length === 0 && (
              <div className="text-center py-14">
                <div className="mx-auto h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 via-violet-500 to-pink-500 opacity-80" />
                <h3 className="mt-6 text-lg font-semibold text-slate-800 dark:text-slate-200">
                  Aucun objet trouvé
                </h3>
                <p className="mt-1 text-slate-600 dark:text-slate-400">
                  Ajustez votre recherche ou ajoutez un nouvel élément.
                </p>
              </div>
            )}

            {/* Table desktop */}
            {!loading && filteredInventaire.length > 0 && (
              <div className="hidden md:block pt-4">
                <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40">
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead>
                        <tr className="bg-slate-50/80 dark:bg-slate-900/80 uppercase tracking-wide text-xs">
                          {[
                            "Photo",
                            "Catégorie",
                            "Nom",
                            "Stock",
                            "Note",
                            "Actions",
                          ].map((h) => (
                            <th
                              key={h}
                              className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-white"
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-sm">
                        {filteredInventaire.map((item) => (
                          <tr
                            key={item.id}
                            className="group transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-800/40"
                          >
                            {/* Photo */}
                            <td className="px-4 py-3">
                              <img
                                src={toAbsolute(item.photo_url) || FALLBACK}
                                alt={item.nom || "Objet"}
                                onError={(e) =>
                                  (e.currentTarget.src = FALLBACK)
                                }
                                className="h-12 w-12 rounded-full object-cover ring-2 ring-white dark:ring-slate-800"
                              />
                            </td>

                            <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                              {item.categorie || "—"}
                            </td>

                            <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">
                              {item.nom || "—"}
                            </td>

                            <td className="px-4 py-3">
                              <span className="inline-flex items-center rounded-md border border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-900/40 px-2 py-0.5 text-sm font-semibold text-slate-800 dark:text-slate-100 min-w-[3rem] justify-center">
                                {item.stock ?? "—"}
                              </span>
                            </td>

                            <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                              {item.note || "—"}
                            </td>

                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <ButtonEdit
                                  onClick={() => handleEditClick(item)}
                                />
                                <ButtonDelete
                                  onClick={() => handleDeleteClick(item.id)}
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Cartes mobile */}
            {!loading && filteredInventaire.length > 0 && (
              <div className="md:hidden space-y-3 pt-4">
                {filteredInventaire.map((item) => (
                  <div
                    key={`card-${item.id}`}
                    className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40 p-4 shadow-sm transition hover:shadow-md"
                  >
                    <div className="flex items-start gap-4">
                      <img
                        src={toAbsolute(item.photo_url) || FALLBACK}
                        alt={item.nom || "Objet"}
                        onError={(e) => (e.currentTarget.src = FALLBACK)}
                        className="h-16 w-16 rounded-xl object-cover ring-2 ring-white dark:ring-slate-800"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 truncate">
                            {item.nom || "—"}
                          </h3>
                          <span className="inline-flex items-center rounded-md border border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-900/40 px-2 py-0.5 text-sm font-semibold text-slate-800 dark:text-slate-100">
                            {item.stock ?? "—"}
                          </span>
                        </div>
                        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                          {item.categorie || "—"}
                        </p>
                        {!!item.note && (
                          <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                            {item.note}
                          </p>
                        )}

                        <div className="mt-3 flex items-center gap-2">
                          <ButtonEdit onClick={() => handleEditClick(item)} />
                          <ButtonDelete
                            onClick={() => handleDeleteClick(item.id)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <ToastContainer />
      </div>

      {/* Modales */}
      {showEditModal && (
        <EditInventaireModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          inventaire={currentInventaire}
          refreshList={fetchInventaire}
        />
      )}
      {showAddModal && (
        <AddInventaireModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          refreshList={fetchInventaire}
        />
      )}
    </section>
  );
}
